import { motion } from "framer-motion";
import {
  Activity,
  ArrowRight,
  Compass,
  HeartHandshake,
  MapPin,
  Sparkles,
  Sprout,
  Users,
  Video,
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import ModernCarousel from "../components/Carousel";
import Reveal from "../components/Reveal";
import useSEO from "../hooks/useSEO";
import "./Home.css";

import img1 from "../img/carusel/img1.webp";
import img2 from "../img/carusel/img2.webp";
import img3 from "../img/carusel/img3.webp";
import img4 from "../img/carusel/img4.webp";
import img5 from "../img/carusel/img5.webp";
import img6 from "../img/carusel/img6.webp";

import ritratto from "../img/home.webp";

// `position` regola il centro di ogni foto (CSS object-position).
// Valori possibili: "center", "top", "bottom", oppure "50% 30%"
// (primo valore = orizzontale, secondo = verticale; 0% = alto/sinistra).
const carouselImages = [
  { src: img1, position: "50% 70%", alt: "Studio della psicologa Anna Antoniozzi a Rieti" },
  { src: img2, position: "50% 60%", alt: "Sala d'attesa dello studio psicologico a Rieti" },
  { src: img3, position: "50% 70%", alt: "Ambiente accogliente per le sedute psicologiche" },
  { src: img4, position: "50% 80%", alt: "Dettaglio dello studio della Dott.ssa Anna Antoniozzi" },
  { src: img5, position: "50% 90%", alt: "Spazio riservato per il colloquio psicologico" },
  { src: img6, position: "50% 80%", alt: "Studio di psicologia a Rieti, in presenza e online" },
];

const perChi = [
  {
    icon: Activity,
    title: "Ansia e stress",
    text: "Ritrovare calma e strumenti concreti per gestire i momenti difficili.",
  },
  {
    icon: Users,
    title: "Difficoltà relazionali",
    text: "Comprendere e trasformare il modo in cui vivi le relazioni.",
  },
  {
    icon: Sparkles,
    title: "Autostima e valore",
    text: "Riconoscere le tue risorse e costruire fiducia in te stesso.",
  },
  {
    icon: Compass,
    title: "Momenti di cambiamento",
    text: "Attraversare le transizioni della vita con maggiore consapevolezza.",
  },
  {
    icon: HeartHandshake,
    title: "Adolescenti e adulti",
    text: "Percorsi calibrati su età, bisogni e obiettivi personali.",
  },
];

const servizi = [
  {
    n: "01",
    icon: MapPin,
    title: "Terapia individuale",
    text: "Percorsi personalizzati in studio a Rieti per ansia, stress, difficoltà relazionali e crescita personale.",
    to: "/servizi",
  },
  {
    n: "02",
    icon: Video,
    title: "Supporto online",
    text: "Sedute su Google Meet, con la stessa cura del setting in presenza, ovunque tu sia.",
    to: "/servizi",
  },
  {
    n: "03",
    icon: Sprout,
    title: "Percorsi per l'autostima",
    text: "Un lavoro su consapevolezza, confini e autoefficacia per rafforzare il tuo valore.",
    to: "/servizi",
  },
];

const percorso = [
  {
    n: "01",
    title: "Primo contatto",
    text: "Mi scrivi o mi chiami: raccontiamo brevemente di cosa hai bisogno.",
  },
  {
    n: "02",
    title: "Colloquio conoscitivo",
    text: "Un primo incontro per conoscerci e capire insieme come procedere.",
  },
  {
    n: "03",
    title: "Percorso su misura",
    text: "Definiamo obiettivi e ritmo degli incontri, adatti alla tua situazione.",
  },
  {
    n: "04",
    title: "Incontri",
    text: "Sedute di 45 minuti, in studio a Rieti o online su Meet.",
  },
];

const Home = () => {
  const navigate = useNavigate();
  useSEO({
    title: "La Psicologa Anna | Psicologa a Rieti e online",
    description:
      "Supporto psicologico professionale a Rieti e online con la Dott.ssa Anna Antoniozzi. Terapia individuale, percorsi per l'autostima e sedute su Meet.",
  });

  return (
    <div className="home">
      {/* HERO */}
      <section className="hero section--paper">
        <div className="container hero__inner">
          <motion.div
            className="hero__text"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          >
            <p className="eyebrow">Psicologa · Rieti &amp; online</p>
            <h1 className="display hero__title">
              Un percorso per diventare{" "}
              <span className="accent-underline">te stesso</span>.
            </h1>
            <p className="lead hero__lead">
              Sono la Dott.ssa Anna Antoniozzi. Ti accompagno, attraverso
              l'ascolto e un lavoro condiviso, a conoscere ed entrare in
              contatto con i tuoi vissuti e le tue emozioni.
            </p>
            <div className="hero__actions">
              <button
                className="btn btn--accent"
                onClick={() => navigate("/prenota-appuntamento")}
              >
                Prenota il primo colloquio
                <ArrowRight className="btn-arrow" size={18} />
              </button>
              <button
                className="btn btn--ghost"
                onClick={() => navigate("/chi-sono")}
              >
                Chi sono
              </button>
            </div>
            <div className="chips hero__chips">
              <span className="chip">Iscritta all'Albo del Lazio</span>
              <span className="chip">In presenza &amp; online</span>
            </div>
          </motion.div>

          <motion.div
            className="hero__media"
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.1 }}
          >
            <div className="hero__photo">
              <img
                src={ritratto}
                alt="Dott.ssa Anna Antoniozzi, psicologa a Rieti"
                fetchPriority="high"
                width="1600"
                height="1200"
              />
            </div>
            <span className="hero__blob" aria-hidden="true" />
          </motion.div>
        </div>
      </section>

      {/* A CHI MI RIVOLGO */}
      <section className="section section--cream">
        <div className="container">
          <Reveal className="section-head">
            <p className="eyebrow">A chi mi rivolgo</p>
            <h2 className="display--md">
              Uno spazio per ciò che stai attraversando
            </h2>
          </Reveal>
          <div className="perchi-grid">
            {perChi.map((item, i) => (
              <Reveal key={item.title} className="perchi-card" delay={i * 0.06}>
                <span className="perchi-icon">
                  <item.icon size={22} strokeWidth={1.75} />
                </span>
                <h3 className="perchi-title">{item.title}</h3>
                <p className="perchi-text">{item.text}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* APPROCCIO - sezione scura */}
      <section className="section section--dark approccio">
        <div className="container container--narrow">
          <Reveal>
            <p className="eyebrow">Il mio approccio</p>
            <h2 className="display--md cta-band__title">
              Una visione profonda della psiche, orientata alla trasformazione.
            </h2>
            <p className="lead approccio__text">
              Lavoro con un approccio fondato sulla psicologia analitica di Carl
              Gustav Jung e sulla psicologia archetipica di James Hillman.
              Significa dare valore all'ascolto, ai simboli e al significato di
              ciò che vivi, per aiutarti a usare al meglio le tue risorse
              cognitive ed emotive.
            </p>
            <Link to="/chi-sono" className="link-arrow approccio__link">
              Scopri il mio percorso
            </Link>
          </Reveal>
        </div>
      </section>

      {/* SERVIZI */}
      <section className="section section--paper">
        <div className="container">
          <Reveal className="section-head">
            <p className="eyebrow">Come posso aiutarti</p>
            <h2 className="display--md">Servizi</h2>
          </Reveal>
          <div className="servizi-grid">
            {servizi.map((s, i) => (
              <Reveal key={s.n} className="servizio" delay={i * 0.08}>
                <div className="servizio__top">
                  <span className="servizio__n">{s.n}</span>
                  <span className="servizio__icon">
                    <s.icon size={24} strokeWidth={1.75} />
                  </span>
                </div>
                <h3 className="servizio__title">{s.title}</h3>
                <p className="servizio__text">{s.text}</p>
                <button className="link-arrow" onClick={() => navigate(s.to)}>
                  Scopri di più
                </button>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* COME FUNZIONA */}
      <section className="section section--sage">
        <div className="container">
          <Reveal className="section-head">
            <p className="eyebrow">Come funziona</p>
            <h2 className="display--md">Un percorso in quattro passi</h2>
          </Reveal>
          <div className="percorso-grid">
            {percorso.map((p, i) => (
              <Reveal key={p.n} className="percorso-step" delay={i * 0.07}>
                <span className="percorso-n">{p.n}</span>
                <h3 className="percorso-title">{p.title}</h3>
                <p className="percorso-text">{p.text}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* LO STUDIO */}
      <section className="section section--paper">
        <div className="container">
          <Reveal className="section-head">
            <p className="eyebrow">Lo studio</p>
            <h2 className="display--md">Un ambiente accogliente e riservato</h2>
          </Reveal>
          <Reveal className="studio-carousel">
            <ModernCarousel
              images={carouselImages}
              autoplaySpeed={5000}
              showArrows
              showDots
            />
          </Reveal>
        </div>
      </section>

      {/* CTA FINALE */}
      <section className="cta-band">
        <div className="container cta-band__inner">
          <Reveal>
            <h2 className="display cta-band__title">
              Il primo passo è parlare.
            </h2>
            <p className="cta-band__text">
              Prenota un primo colloquio conoscitivo: capiremo insieme come
              posso esserti utile.
            </p>
            <div className="cta-band__actions">
              <button
                className="btn btn--accent"
                onClick={() => navigate("/prenota-appuntamento")}
              >
                Prenota ora
                <ArrowRight className="btn-arrow" size={18} />
              </button>
              <a href="tel:+393791566652" className="btn btn--ghost">
                +39 379 156 6652
              </a>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Home;
